/**
 * WorkflowSectionEditor
 *
 * Editor for the "Section Entry" node — a named entry point in the workflow
 * that AI nodes can jump to based on detected intent.
 *
 * Place this node at the start of a section (e.g. "Returns Process").
 * AI nodes configured with this section as a jump target will inject a
 * jump_to_{name} tool into their prompt, letting the AI route here when
 * the conversation matches the described intent.
 *
 * After the section runs, control returns to the AI node selected here
 * (or the calling AI implicitly if left on Auto).
 */
import * as React from "react";
import { useContext, useMemo } from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { WorkflowNode } from "../../types";
import { WorkflowContext } from "@/context/WorkflowContext";
import useAIConfigs from "@/hooks/useAIConfigs";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

const AUTO_RETURN = 0;

export default function WorkflowSectionEditor({ node, onUpdate }: Props) {
  const { workflow } = useContext(WorkflowContext);
  const { aiConfigurations } = useAIConfigs();

  // O(1) config name lookups — avoids repeated .find() inside map
  const configNameById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const c of aiConfigurations) map[c.id] = c.name;
    return map;
  }, [aiConfigurations]);

  // TransferToAI nodes in this workflow (excluding self) — candidates for return target
  const aiNodes = useMemo(() => {
    if (!workflow) return [];
    return Object.values(workflow.nodes).filter(
      (n) => n.type === "action" && n.actionType === "transferToAI" && n.id !== node.id
    );
  }, [workflow, node.id]);

  const returnToNodeId = node.returnToNodeId ?? AUTO_RETURN;
  const returnNode = returnToNodeId !== AUTO_RETURN ? aiNodes.find((n) => n.id === returnToNodeId) : undefined;
  const selectedReturnLabel = returnNode
    ? (configNameById[returnNode.aiConfigId!] || returnNode.name || `AI node ${returnNode.id}`)
    : null;

  const selectOptions: { value: number; label: string }[] = [
    { value: AUTO_RETURN, label: "Auto — return to the calling AI" },
    ...aiNodes.map((n) => ({
      value: n.id,
      label: configNameById[n.aiConfigId!] || n.name || `AI node ${n.id}`,
    })),
  ];

  const toolName = (node.sectionName || "section_name")
    .toLowerCase()
    .replace(/\s+/g, "_");

  return (
    <>
      {/* How it works banner */}
      <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5 text-xs text-amber-800 leading-relaxed">
        <p className="font-semibold mb-1">How Section Entry works</p>
        <ol className="list-decimal list-inside space-y-1 text-amber-700">
          <li>Name this section (e.g. "Returns Process").</li>
          <li>
            In an AI node, add this section as a{" "}
            <span className="font-medium">Jump Target</span> and describe when to jump here.
          </li>
          <li>The AI detects the intent and jumps to this node.</li>
          <li>
            End this section with a <span className="font-medium">Transfer to AI</span> node
            pointing back to the AI you want to resume — usually the one that jumped here.
          </li>
        </ol>
      </div>

      {/* Section Name */}
      <FormGroup label="Section Name">
        <input
          type="text"
          className="input text-sm"
          placeholder='e.g. "Returns Process", "Billing Support", "Order Lookup"'
          value={node.sectionName ?? ""}
          onChange={(e) => onUpdate({ sectionName: e.target.value })}
        />
        <p className="text-xs text-slate-500 mt-1">
          This name appears in AI nodes' Jump Target selector and generates the tool name{" "}
          <code className="bg-slate-100 px-1 rounded font-mono">jump_to_{toolName}</code>.
        </p>
      </FormGroup>

      {/* Section Description */}
      <FormGroup label="Description">
        <textarea
          className="input text-sm resize-y min-h-16"
          placeholder="What does this section handle? This helps document the workflow."
          value={node.sectionDescription ?? ""}
          onChange={(e) => onUpdate({ sectionDescription: e.target.value })}
        />
      </FormGroup>

      {/* Return path — AI node selector */}
      <FormGroup label="Return to AI after this section">
        <Select
          options={selectOptions}
          value={returnToNodeId}
          onChange={(value) =>
            onUpdate({ returnToNodeId: Number(value) || undefined })
          }
        />
        <p className="text-xs text-slate-500 mt-1">
          {aiNodes.length === 0 ? (
            <>
              No AI nodes in this workflow yet. Add a{" "}
              <span className="font-medium">Transfer to AI</span> node first, then return
              here to select it. End this section with that same Transfer to AI node — select
              the same bot config the customer started with — so control flows back naturally.
              You can also end with{" "}
              <span className="font-medium">Transfer to Agents</span> or{" "}
              <span className="font-medium">Close Conversation</span>.
            </>
          ) : selectedReturnLabel ? (
            <>
              End this section with a <span className="font-medium">Transfer to AI</span>{" "}
              node and select{" "}
              <span className="font-medium">{selectedReturnLabel}</span> — that sends the
              conversation back to your main bot.
            </>
          ) : (
            <>
              <span className="font-medium">Auto</span> is recommended — the runtime returns
              control to whichever AI jumped here. End the section with a{" "}
              <span className="font-medium">Transfer to AI</span> node pointing to that same
              bot config (the one that jumped here). Only pick a specific AI if this section
              should always hand off to a different bot regardless of who triggered the jump.
            </>
          )}
        </p>
      </FormGroup>
    </>
  );
}
